import { jsPDF } from "jspdf";
import type { FormacionCertificado, FormacionConstanciaDictado } from "@/hooks/api/comunidad";
import { loadLogoAsPngDataUrl } from "@/utils/generateConstanciaPdf";
import poppinsRegularUrl from "@/assets/fonts/poppins/Poppins-Regular.ttf";
import poppinsSemiBoldUrl from "@/assets/fonts/poppins/Poppins-SemiBold.ttf";
import poppinsBoldUrl from "@/assets/fonts/poppins/Poppins-Bold.ttf";

/**
 * Certificado de participación en una formación de Comunidad (A4 apaisado). Lo descarga
 * desde "Tu participación en la formación" quien participó (≥75% del encuentro); los datos
 * vienen del backend (`participacion.certificado`). Redacción tomada del certificado de los
 * talleres del año pasado, sin la marca "Entre Psicos". Sin firma.
 * La duración es la CONFIRMADA en el panel al subir el informe de Meet de esa edición.
 */

type RGB = [number, number, number];
const NAVY: RGB = [45, 32, 73]; // #2D2049
const VIOLET: RGB = [102, 80, 144]; // #665090
const LAVENDER: RGB = [132, 117, 179]; // #8475B3
const LILAC: RGB = [201, 188, 232]; // #C9BCE8

/**
 * Poppins, la tipografía de la marca (manual de marca: Poppins en docs y PDFs). jsPDF no puede
 * usar la fuente de la página: necesita los .ttf, que viven en `assets/fonts/poppins` (SIL Open
 * Font License, ver OFL.txt) y se bajan recién al armar un certificado.
 */
export interface PoppinsFonts {
  regular: string;
  semibold: string;
  bold: string;
}

const FONT = "Poppins";

async function ttfToBase64(url: string): Promise<string> {
  const buf = new Uint8Array(await (await fetch(url)).arrayBuffer());
  let binary = "";
  for (let i = 0; i < buf.length; i += 0x8000) {
    binary += String.fromCharCode.apply(null, Array.from(buf.subarray(i, i + 0x8000)));
  }
  return btoa(binary);
}

/** null si no se pudieron cargar: el certificado sale igual, con Helvetica. */
export async function loadPoppinsFonts(): Promise<PoppinsFonts | null> {
  try {
    const [regular, semibold, bold] = await Promise.all([
      ttfToBase64(poppinsRegularUrl),
      ttfToBase64(poppinsSemiBoldUrl),
      ttfToBase64(poppinsBoldUrl),
    ]);
    return { regular, semibold, bold };
  } catch {
    return null;
  }
}

/** Registra Poppins en el documento y devuelve cómo pedir cada peso (o Helvetica si no hay). */
function setupFonts(doc: jsPDF, fonts: PoppinsFonts | null) {
  if (!fonts) {
    return {
      regular: () => doc.setFont("helvetica", "normal"),
      semibold: () => doc.setFont("helvetica", "bold"),
      bold: () => doc.setFont("helvetica", "bold"),
    };
  }
  doc.addFileToVFS("Poppins-Regular.ttf", fonts.regular);
  doc.addFont("Poppins-Regular.ttf", FONT, "normal");
  doc.addFileToVFS("Poppins-SemiBold.ttf", fonts.semibold);
  doc.addFont("Poppins-SemiBold.ttf", FONT, "semibold");
  doc.addFileToVFS("Poppins-Bold.ttf", fonts.bold);
  doc.addFont("Poppins-Bold.ttf", FONT, "bold");
  return {
    regular: () => doc.setFont(FONT, "normal"),
    semibold: () => doc.setFont(FONT, "semibold"),
    bold: () => doc.setFont(FONT, "bold"),
  };
}

/** Subtítulo de cada certificado (el mismo texto en el PDF, la imagen y la miniatura). */
export const CERTIFICADO_HEADING = {
  participacion: "Certificado de participación en la formación interna para psicólogos/as",
  dictado: "Certificado de dictado de la formación interna para psicólogos/as",
} as const;

/** "octubre de 2026", en hora de Argentina: la constancia de dictado vale para todas las
 *  ediciones de la formación, así que lleva solo mes y año. */
export function formatCertificadoMonth(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("es-AR", {
    timeZone: "America/Argentina/Buenos_Aires",
    month: "long",
    year: "numeric",
  });
}

/** "14 de octubre de 2026", en hora de Argentina. */
export function formatCertificadoDate(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("es-AR", {
    timeZone: "America/Argentina/Buenos_Aires",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/**
 * Superficie de dibujo del certificado. El diseño se escribe UNA vez (`drawCertificado`) y se
 * dibuja igual en el PDF (jsPDF) y en la imagen para LinkedIn (canvas): así nunca se desfasan.
 * Medidas en puntos de A4 apaisado (842 × 595).
 */
type Weight = "regular" | "semibold" | "bold";
interface TextStyle {
  weight: Weight;
  size: number;
  color: RGB;
  charSpace?: number;
  lineHeightFactor?: number;
}
interface CertSurface {
  roundedRect(x: number, y: number, w: number, h: number, r: number, color: RGB, lineWidth: number): void;
  line(x1: number, y1: number, x2: number, y2: number, color: RGB, lineWidth: number): void;
  image(x: number, y: number, w: number, h: number): void;
  /** Centrado en `cx`; `y` es la línea base del primer renglón. */
  centeredText(text: string | string[], cx: number, y: number, style: TextStyle): void;
  textWidth(text: string, style: TextStyle): number;
}

const PAGE_W = 842;
const PAGE_H = 595;

/**
 * Corta el texto en renglones de ancho parejo (no deja una palabra suelta al final): si entra
 * en dos renglones, busca el corte que minimiza el renglón más ancho.
 */
function balancedLines(surface: CertSurface, text: string, maxWidth: number, style: TextStyle): string[] {
  const words = text.split(" ");
  const greedy: string[] = [];
  let current = "";
  for (const w of words) {
    const next = current ? `${current} ${w}` : w;
    if (current && surface.textWidth(next, style) > maxWidth) {
      greedy.push(current);
      current = w;
    } else {
      current = next;
    }
  }
  if (current) greedy.push(current);
  if (greedy.length !== 2) return greedy;
  let best = greedy;
  let bestWidth = Math.max(...greedy.map((l) => surface.textWidth(l, style)));
  for (let i = 1; i < words.length; i++) {
    const lines = [words.slice(0, i).join(" "), words.slice(i).join(" ")];
    const widest = Math.max(...lines.map((l) => surface.textWidth(l, style)));
    if (widest <= maxWidth && widest < bestWidth) {
      best = lines;
      bestWidth = widest;
    }
  }
  return best;
}

/** Lo que cambia entre el certificado de participación y el de dictado; el resto es igual. */
interface CertContent {
  heading: string;
  formacionTitle: string;
  name: string;
  surname: string;
  /** Renglón de abajo del nombre (fecha, dedicación). */
  details: string | null;
}

function participacionContent(data: FormacionCertificado): CertContent {
  const date = formatCertificadoDate(data.startsAt);
  const details = [date, data.durationMinutes ? `Dedicación: ${data.durationMinutes} minutos` : null]
    .filter(Boolean)
    .join("   ·   ");
  return { ...data, heading: CERTIFICADO_HEADING.participacion, details: details || null };
}

function dictadoContent(data: FormacionConstanciaDictado): CertContent {
  return { ...data, heading: CERTIFICADO_HEADING.dictado, details: formatCertificadoMonth(data.startsAt) };
}

function drawCertificado(s: CertSurface, data: CertContent, hasLogo: boolean) {
  const W = PAGE_W;
  const H = PAGE_H;
  const cx = W / 2;

  // Marco: línea lavanda y, adentro, una más fina.
  s.roundedRect(28, 28, W - 56, H - 56, 10, LAVENDER, 2);
  s.roundedRect(38, 38, W - 76, H - 76, 7, LILAC, 0.75);

  // Encabezado: logo grande, centrado y cerca del margen de arriba + "PSI MAMMOLITI".
  let y = 62;
  const logoSize = 92;
  if (hasLogo) {
    s.image(cx - logoSize / 2, y, logoSize, logoSize);
    y += logoSize + 20;
  }
  s.centeredText("PSI MAMMOLITI", cx, y, { weight: "semibold", size: 10, color: VIOLET, charSpace: 1.6 });

  // "Certificado de participación en la formación" (secundario) y, como protagonista, el
  // nombre de la formación en mayúsculas y negrita (puede ocupar dos o tres renglones).
  y += 64;
  s.centeredText(data.heading, cx, y, {
    weight: "regular",
    size: 14,
    color: VIOLET,
  });

  y += 44;
  const titleStyle: TextStyle = {
    weight: "bold",
    size: data.formacionTitle.length > 70 ? 20 : 24,
    color: NAVY,
    lineHeightFactor: 1.25,
  };
  const titleLines = balancedLines(s, `“${data.formacionTitle.toUpperCase()}”`, W - 200, titleStyle);
  s.centeredText(titleLines, cx, y, titleStyle);
  y += (titleLines.length - 1) * titleStyle.size * 1.25;

  // "Otorgado a" + nombre, con una línea debajo.
  y += 70;
  s.centeredText("OTORGADO A", cx, y, { weight: "semibold", size: 9, color: LAVENDER, charSpace: 1.5 });

  y += 46;
  const fullName = `${data.name} ${data.surname}`.trim();
  const nameStyle: TextStyle = { weight: "bold", size: fullName.length > 32 ? 28 : 34, color: NAVY };
  s.centeredText(fullName, cx, y, nameStyle);
  const nameWidth = Math.min(s.textWidth(fullName, nameStyle) + 60, W - 200);
  s.line(cx - nameWidth / 2, y + 14, cx + nameWidth / 2, y + 14, LILAC, 1.2);

  // Fecha (y dedicación, en el de participación).
  y += 48;
  if (data.details) s.centeredText(data.details, cx, y, { weight: "regular", size: 11, color: VIOLET });
}

export function renderCertificadoFormacionPdf(
  data: FormacionCertificado,
  logoDataUrl: string | null,
  fonts: PoppinsFonts | null = null,
): jsPDF {
  return renderCertPdf(participacionContent(data), logoDataUrl, fonts);
}

/** Certificado de dictado (uno por formación): mismo diseño, "Certificado de dictado…". */
export function renderConstanciaDictadoPdf(
  data: FormacionConstanciaDictado,
  logoDataUrl: string | null,
  fonts: PoppinsFonts | null = null,
): jsPDF {
  return renderCertPdf(dictadoContent(data), logoDataUrl, fonts);
}

function renderCertPdf(content: CertContent, logoDataUrl: string | null, fonts: PoppinsFonts | null): jsPDF {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const font = setupFonts(doc, fonts);
  const applyStyle = (st: TextStyle) => {
    font[st.weight]();
    doc.setFontSize(st.size);
    doc.setTextColor(...st.color);
  };
  drawCertificado(
    {
      roundedRect: (x, y, w, h, r, color, lw) => {
        doc.setDrawColor(...color);
        doc.setLineWidth(lw);
        doc.roundedRect(x, y, w, h, r, r);
      },
      line: (x1, y1, x2, y2, color, lw) => {
        doc.setDrawColor(...color);
        doc.setLineWidth(lw);
        doc.line(x1, y1, x2, y2);
      },
      image: (x, y, w, h) => {
        if (logoDataUrl) doc.addImage(logoDataUrl, "PNG", x, y, w, h);
      },
      centeredText: (text, cx, y, st) => {
        applyStyle(st);
        doc.text(text, cx, y, { align: "center", charSpace: st.charSpace, lineHeightFactor: st.lineHeightFactor });
      },
      textWidth: (text, st) => {
        applyStyle(st);
        return doc.getTextWidth(text);
      },
    },
    content,
    !!logoDataUrl,
  );
  return doc;
}

/** Nombre del archivo: "Certificado - <formación> - <nombre>.<ext>" (o "Certificado de dictado - …"). */
function certificadoFileName(
  data: Pick<FormacionCertificado, "formacionTitle" | "name" | "surname">,
  ext: "pdf" | "png",
  prefix = "Certificado",
): string {
  const clean = (s: string) => s.replace(/[\\/:*?"<>|]/g, "").trim();
  const shortTitle = clean(data.formacionTitle.split(":")[0]);
  return `${prefix} - ${shortTitle} - ${clean(`${data.name} ${data.surname}`)}.${ext}`;
}

/** Arma el certificado y lo descarga en PDF (el "Descargar" de siempre). */
export async function downloadCertificadoFormacion(data: FormacionCertificado): Promise<void> {
  // En alta resolución: en el certificado el logo va grande.
  const [logo, fonts] = await Promise.all([loadLogoAsPngDataUrl(480), loadPoppinsFonts()]);
  const doc = renderCertificadoFormacionPdf(data, logo, fonts);
  doc.save(certificadoFileName(data, "pdf"));
}

/** Arma el certificado de dictado y lo descarga en PDF ("Mis formaciones dictadas"). */
export async function downloadConstanciaDictado(data: FormacionConstanciaDictado): Promise<void> {
  const [logo, fonts] = await Promise.all([loadLogoAsPngDataUrl(480), loadPoppinsFonts()]);
  const doc = renderConstanciaDictadoPdf(data, logo, fonts);
  doc.save(certificadoFileName(data, "pdf", "Certificado de dictado"));
}

/** Familia propia para el canvas, así no pisa la Poppins que pueda usar la página. */
const CANVAS_FONT = "PoppinsCertificado";
const CANVAS_WEIGHTS: Record<Weight, number> = { regular: 400, semibold: 600, bold: 700 };

async function loadCanvasFonts(): Promise<boolean> {
  try {
    const faces = [
      new FontFace(CANVAS_FONT, `url(${poppinsRegularUrl})`, { weight: "400" }),
      new FontFace(CANVAS_FONT, `url(${poppinsSemiBoldUrl})`, { weight: "600" }),
      new FontFace(CANVAS_FONT, `url(${poppinsBoldUrl})`, { weight: "700" }),
    ];
    await Promise.all(faces.map((f) => f.load()));
    faces.forEach((f) => document.fonts.add(f));
    return true;
  } catch {
    return false;
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("No se pudo cargar la imagen"));
    img.src = src;
  });
}

/** El mismo certificado como imagen PNG (alta resolución), para adjuntarlo en LinkedIn. */
export async function renderCertificadoFormacionPng(data: FormacionCertificado): Promise<Blob> {
  const SCALE = 3; // 842 × 595 pt → 2526 × 1785 px
  const [logoUrl, hasPoppins] = await Promise.all([loadLogoAsPngDataUrl(480), loadCanvasFonts()]);
  const logo = logoUrl ? await loadImage(logoUrl).catch(() => null) : null;
  const canvas = document.createElement("canvas");
  canvas.width = PAGE_W * SCALE;
  canvas.height = PAGE_H * SCALE;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas no disponible");
  ctx.scale(SCALE, SCALE);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, PAGE_W, PAGE_H);
  const rgb = (c: RGB) => `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
  const family = hasPoppins ? `"${CANVAS_FONT}"` : "Helvetica, Arial, sans-serif";
  // `letterSpacing` del canvas: lo tienen los navegadores actuales; si falta, sale sin espaciado.
  const spacing = ctx as CanvasRenderingContext2D & { letterSpacing?: string };
  const applyStyle = (st: TextStyle) => {
    ctx.font = `${CANVAS_WEIGHTS[st.weight]} ${st.size}px ${family}`;
    ctx.fillStyle = rgb(st.color);
    if ("letterSpacing" in spacing) spacing.letterSpacing = `${st.charSpace ?? 0}px`;
  };
  drawCertificado(
    {
      roundedRect: (x, y, w, h, r, color, lw) => {
        ctx.strokeStyle = rgb(color);
        ctx.lineWidth = lw;
        ctx.beginPath();
        ctx.roundRect(x, y, w, h, r);
        ctx.stroke();
      },
      line: (x1, y1, x2, y2, color, lw) => {
        ctx.strokeStyle = rgb(color);
        ctx.lineWidth = lw;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      },
      image: (x, y, w, h) => {
        if (logo) ctx.drawImage(logo, x, y, w, h);
      },
      centeredText: (text, cx, y, st) => {
        applyStyle(st);
        ctx.textAlign = "center";
        ctx.textBaseline = "alphabetic";
        const lines = Array.isArray(text) ? text : [text];
        lines.forEach((l, i) => ctx.fillText(l, cx, y + i * st.size * (st.lineHeightFactor ?? 1.15)));
      },
      textWidth: (text, st) => {
        applyStyle(st);
        return ctx.measureText(text).width;
      },
    },
    participacionContent(data),
    !!logo,
  );
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("No se pudo armar la imagen"))), "image/png"),
  );
}

/** Descarga el certificado como imagen ("Descargar imagen para LinkedIn"). */
export async function downloadCertificadoFormacionImagen(data: FormacionCertificado): Promise<void> {
  const blob = await renderCertificadoFormacionPng(data);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = certificadoFileName(data, "png");
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Texto sugerido para la publicación (la psicóloga lo puede cambiar antes de publicar). */
export function linkedinPostText(data: FormacionCertificado): string {
  return `Participé de la formación “${data.formacionTitle}” de Psi Mammoliti 🎓 Seguir aprendiendo entre colegas también es parte de cuidar a quienes acompañamos. 💜`;
}

/**
 * "Publicarlo": abre el cuadro de nueva publicación de LinkedIn con el texto sugerido. El
 * certificado lo adjunta ella (LinkedIn no deja que una página externa adjunte archivos).
 */
export function linkedinPostUrl(data: FormacionCertificado): string {
  return `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(linkedinPostText(data))}`;
}
